import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { VelvetCommonContent } from "../../../../modules/content/components/VelvetCommonContent/VelvetCommonContent";
import { VelvetMedia } from "../../../../modules/content/components/VelvetMedia/VelvetMedia";
import { VelvetRichText } from "../../../../modules/content/components/VelvetRichText/VelvetRichText";
import { VelvetSection } from "../../../../modules/content/components/VelvetSection/VelvetSection";
import type { ITeamMemberGridBlockProps } from "./TeamMemberGridBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;

export const TeamMemberGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITeamMemberGridBlockProps) => {
  if (!items?.length) return null;
  const Link = getComponent("Link");

  return (
    <VelvetSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-16 md:gap-24">
        <VelvetCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid gap-x-5 gap-y-14", columns[columnCount || 3])}>
          {items.map((member, index) => (
            <article className={cn("group", index % 2 === 1 && "lg:mt-24")} key={index}>
              <VelvetMedia
                aspectRatio="3:4"
                className="velvet-arch border border-parchment/20 bg-plum"
                imageClassName="saturate-[0.75] transition-[filter,transform] duration-500 ease-out group-hover:scale-[1.02] group-hover:saturate-100"
                media={member.portrait.image}
                sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
              />
              <div className="border-t border-parchment/30 pt-5">
                <h3 className="font-velvet-display text-4xl font-black uppercase leading-[0.88] tracking-[-0.035em]">
                  {member.name}
                </h3>
                {member.role ? (
                  <p className="mt-3 font-velvet-body text-base italic text-thread">
                    {member.role}
                  </p>
                ) : null}
                {member.biography ? (
                  <VelvetRichText
                    className="mt-4 text-base text-smoke md:text-lg"
                    content={member.biography}
                  />
                ) : null}
                {member.links?.length ? (
                  <div className="mt-5 flex flex-wrap gap-5 border-t border-parchment/20 pt-3">
                    {member.links.map((link, linkIndex) => (
                      <Link
                        aria-label={link.accessibilityLabel || undefined}
                        className="inline-flex min-h-11 items-center font-velvet-body text-base italic text-thread underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                        href={link.href}
                        key={linkIndex}
                        rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                        target={link.openInNewTab ? "_blank" : undefined}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </VelvetSection>
  );
};

TeamMemberGridBlock.displayName = "Block::TeamMemberGridBlock";
